/**
 * NirvanaPay Webhook Schemas
 *
 * Raw webhook/status payloads from NirvanaPay provider.
 * Used by: payment-service/src/webhooks/nirvanapay.ts
 *
 * IMPORTANT: NirvanaPay uses GET request, not POST!
 *
 * Endpoint:
 *   GET /webhook/nirvana?id={clientId}
 *
 * The webhook only contains clientId - we must call checkStatus() to get actual status.
 */
import { z } from 'zod';
// =============================================================================
// NIRVANAPAY WEBHOOK QUERY
// Source: payment-service/src/webhooks/nirvanapay.ts:61-62
// =============================================================================
/**
 * NirvanaPay webhook query params
 *
 * Example: GET /webhook/nirvana?id=nirv-test-22222
 */
export const nirvanaWebhookQuerySchema = z.object({
    /** Client ID (our internal transaction reference stored in NirvanaPay) */
    id: z.string().optional(),
});
// =============================================================================
// NIRVANAPAY STATUS CHECK RESPONSE
// Source: payment-service/src/providers/nirvanapay/api.ts
// =============================================================================
/**
 * NirvanaPay status check response (formatted)
 * This is what we get after calling checkStatus()
 */
export const nirvanaStatusResponseSchema = z.object({
    /** Transaction ID */
    id: z.string(),
    /** Raw status: SUCCESS, ERROR, CREATED, ACCEPTED, PENDING, APPEAL */
    status: z.string(),
    /** Amount in local currency */
    amount: z.number().optional(),
    /** When payment was completed */
    paidAt: z.string().optional(),
    /** Error message if failed */
    errorMessage: z.string().optional(),
}).passthrough();
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/**
 * Checks if clientId is present in query
 */
export function hasNirvanaClientId(query) {
    return !!query.id;
}
// =============================================================================
// STATUS MAPPING
// =============================================================================
/**
 * Maps NirvanaPay raw status to normalized WebhookStatus
 * - SUCCESS/COMPLETED → completed
 * - ERROR/FAILED → failed
 * - CREATED/ACCEPTED/PENDING → pending
 * - APPEAL → pending (under dispute, not terminal)
 */
export function mapNirvanaStatus(status) {
    switch (status?.toUpperCase()) {
        case 'SUCCESS':
        case 'COMPLETED':
            return 'completed';
        case 'ERROR':
        case 'FAILED':
            return 'failed';
        case 'CREATED':
        case 'ACCEPTED':
        case 'PENDING':
            return 'pending';
        case 'APPEAL':
            return 'pending'; // Under dispute, not terminal
        default:
            return 'pending';
    }
}
//# sourceMappingURL=nirvanapay.js.map